/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Brand } from '@/lib/brand/brand';

// Imported rather than written as path strings, and that is load-bearing -- see
// "THE PATHS ARE IMPORTS" in the note below. Same mechanism as the favicon in
// src/root.tsx, which is the existing precedent for a public/ asset in <head>.
import neuzeitRegular from '/fonts/nm/neuzeit-grotesk/3A2553_2_0.woff2';
import neuzeitBold from '/fonts/nm/neuzeit-grotesk/3A2553_3_0.woff2';
import futuraPt from '/fonts/nm/futura-pt/3A2553_0_0.woff2';
import larsseit from '/fonts/bg/Larsseit.woff2';
import larsseitLight from '/fonts/bg/Larsseit-light.woff2';
import larsseitBold from '/fonts/bg/Larsseit-bold.woff2';

/**
 * CRITICAL FONT FILES, PER BRAND.
 *
 * PROVENANCE OF EVERY VALUE BELOW, said plainly because it is unusual for a
 * per-brand map here: NONE of it is measured off live. The file paths are copied
 * from our own stylesheet, arm by arm — src/theme/base.css:42, :51 and :33 for
 * the NM arm, and :107, :97 and :117 for the BG arm. The widths and the
 * above-the-fold census are measurements of OUR app on the dev server at 1728,
 * five to six cold runs each, guarded on href / clientWidth / data-brand.
 *
 * Live's own metrics are unmeasured here and do not need to be. What live
 * settles is the SETTLED state, and that is already recorded as exact parity —
 * ink to ink at 1728, "Sort By" and "Best Match" delta 0.0 on both sides with a
 * zero noise floor. This map exists because the TRANSIENT state diverges from
 * that settled agreement, which is a fact about our load order rather than about
 * live's type. Nothing here should ever be used as evidence about live.
 *
 * `theme/base.css` declares ten faces with `font-display: swap`. During the swap
 * window the fallback stack paints, and our text is measurably WIDER than live —
 * the only state in which it is. Measured cold at 1728 on the dev server, five
 * runs per element, each element taking exactly two values and nothing between
 * (two states, not jitter):
 *
 *     BG  /BergdorfGoodman/search?q=shoes
 *         "Sort By"      fallback 49.47 -> settled 46.03   (+3.44)
 *         "Best Match"   fallback 75.84 -> settled 72.83   (+3.01)
 *     NM  /NeimanMarcus/search?q=shoes
 *         "Our Stores"   fallback 65.75 -> settled 57.58   (+8.17)
 *         "Sign in"      fallback 39.75 -> settled 34.23   (+5.52)
 *
 * The cause is request ORDER, not size. Every font resource reported
 * `initiatorType: "css"` on every run: the browser cannot know a face is needed
 * until the stylesheet has downloaded, parsed, and matched an element — so the
 * font request is serialised behind the whole CSS critical path. A preload is
 * discovered by the HTML preload scanner instead, which starts the fetch in
 * parallel with the stylesheet.
 *
 * WHY THESE SIX FILES AND NOT THE OTHER FOUR. Censused at 1728 across five
 * archetype surfaces per brand (home, PLP, PDP, search, cart), recording which
 * font files the browser actually requests and how many characters each resolved
 * face paints above the fold:
 *
 *     NM   neuzeit-grotesk 400   requested 5/5   467-995 ATF chars   the body face
 *          neuzeit-grotesk 700   requested 5/5    37-107 ATF chars   ticker, emphasis
 *          futura-pt 400         requested 5/5      7-38 ATF chars   header account row
 *          Romie Regular         requested 1/5         0 ATF chars   home only, below fold
 *          Romie Italic          requested 1/5         0 ATF chars   home only, below fold
 *          Romie Bold / BoldIt.  requested 0/5         -             never requested
 *     BG   Larsseit 400          requested 5/5   528-891 ATF chars   the body face
 *          Larsseit 700 (bold)   requested 5/5     47-65 ATF chars   tabs, emphasis
 *          Larsseit 300 (light)  requested 5/5      4-35 ATF chars   the PLP / SRP h1
 *
 * The four Romie faces are excluded, and that exclusion is the point. Romie is
 * requested on ONE of five NM surfaces and paints no glyph above the fold even
 * there, so preloading it would spend header bytes on four of five pages and
 * produce the browser's "preloaded but not used within a few seconds" warning on
 * each — which is precisely the defect that got the template's Sen preload
 * removed (see the note in `src/root.tsx` links()).
 *
 * Larsseit-light is the marginal keep: 53KB for as few as 4 characters above the
 * fold. Character count understates it — those characters are BG's PLP and search
 * h1, the single largest piece of ink on the page — and the file is requested on
 * 5 of 5 surfaces regardless, so preloading it moves bytes rather than adding
 * them. Drop it if the critical path ever needs the room; it is the first one to
 * go.
 *
 * THE PROPERTY THAT MAKES THIS SAFE, and the difference from the Sen preload:
 * every file listed here is requested by the CSS on every surface of its brand
 * anyway. The preload is BYTE-NEUTRAL. It changes when the fetch starts, never
 * whether it happens, so it cannot produce an unused-preload warning and cannot
 * add weight to a page that would not have carried it.
 *
 * THE PATHS ARE IMPORTS, NOT STRINGS, AND THAT IS THE WHOLE DEPLOY STORY. The
 * first version of this file hardcoded `/fonts/bg/Larsseit.woff2` and would have
 * been correct on localhost and WRONG on the deployed site -- the exact class of
 * defect that only appears once deployed.
 *
 * Read out of the production build rather than assumed. `pnpm build` rewrites the
 * stylesheet's own references to be RELATIVE to the emitted sheet:
 *
 *     build/client/assets/index-*.css       url(../fonts/bg/Larsseit.woff2)
 *
 * and it rewrites imported public assets to carry the runtime bundle prefix, the
 * same treatment the favicon gets:
 *
 *     build/server/assets/server-build-*.cjs
 *         favicon = "" + (window._BUNDLE_PATH ...) + "favicon.ico"
 *
 * On MRT the app is served under a bundle path, so both of those resolve to
 * `<bundle>/fonts/...` while a hardcoded `/fonts/...` resolves to the ORIGIN
 * root. Different URL: the preload would 404 or fetch a second copy, the browser
 * would warn that the preload went unused, and none of it is visible on a dev
 * server where the bundle prefix is empty and the two forms coincide.
 *
 * Importing puts these through the identical pipeline as the stylesheet, so the
 * preload and the `url()` request one URL and the browser reuses one response.
 * `src/lib/fonts.test.ts` asserts each import specifier against base.css so the
 * two cannot drift apart.
 *
 * These are declared in `Layout` rather than root's `links()` because
 * `LinksFunction` takes NO arguments in React Router v7 — see
 * `react-router/dist/development/lib/types/internal.d.ts`, `LinksFunction: () =>
 * LinkDescriptor[]` — so it cannot reach the resolved site and cannot be brand
 * aware. `Layout` already resolves the brand to stamp `data-brand` on <html> and
 * already renders <head> directly, so it is where brand-dependent head content
 * belongs.
 */
export const CRITICAL_FONT_PRELOADS: Record<Brand, readonly string[]> = {
    nm: [neuzeitRegular, neuzeitBold, futuraPt],
    bg: [larsseit, larsseitLight, larsseitBold],
};
